import React from 'react'
import { useController, UseControllerProps } from 'react-hook-form'
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormControl,
} from '@/components/ui/form'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { ChevronDown, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useEffect, useState } from 'react'
import { getUsers } from '@/api/usersAPI'
import { User } from '@/interfaces/vehicle'

export interface ISelectFormData {
  id: string | number
  title: string
  subtitle?: string
}

interface Props {
  name: string
  label?: string
  data: ISelectFormData[]
  defaultValue?: User
}

export const SelectFormUsers = React.forwardRef<
  HTMLDivElement,
  Props & UseControllerProps
>((props, ref) => {
  const { control, name, label, data, defaultValue } = props

  const [users, setUsers] = useState(data)
  const { fieldState, field } = useController(props)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selected, setSelected] = useState<ISelectFormData | undefined>(
    defaultValue
      ? {
          id: defaultValue.id,
          title: defaultValue.displayName,
          subtitle: defaultValue.email,
        }
      : undefined
  )
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    setUsers(data)
  }, [data])

  // Sync selected state with form value
  useEffect(() => {
    if (field.value) {
      const userData = data.find((user) => user.id === field.value.id)
      if (userData) {
        setSelected(userData)
      }
    } else {
      setSelected(undefined)
    }
  }, [field.value, data])

  const GetUsersData = async (search: string) => {
    const response = await getUsers({
      page: 1,
      per_page: 10,
      search: search,
    })
    setUsers(
      response.map(({ id, displayName, email }) => ({
        id: id,
        title: displayName,
        subtitle: email,
      }))
    )
  }

  useEffect(() => {
    GetUsersData(searchQuery)
  }, [searchQuery])

  const filteredData = users.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleSelect = (selectedItem: ISelectFormData) => {
    setIsPopoverOpen(false)
    setSearchQuery('')
    setSelected(selectedItem)
    field.onChange({
      id: selectedItem.id,
      displayName: selectedItem.title,
      email: selectedItem.subtitle,
    })
  }
  return (
    <div className={'w-full'} ref={ref}>
      <FormField
        control={control}
        name={name}
        render={() => (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <Popover onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    aria-expanded={isPopoverOpen}
                    className="h-fit w-full justify-between font-normal"
                    role="combobox"
                    variant="outline"
                  >
                    {selected ? (
                      <div className="flex flex-col items-start">
                        <span>{selected.title}</span>
                        {selected.subtitle && (
                          <span className="text-xs text-muted-foreground">
                            {selected.subtitle}
                          </span>
                        )}
                      </div>
                    ) : (
                      'Seleccionar...'
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <div className="px-3 pt-3">
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        className="pl-9"
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar..."
                        value={searchQuery}
                      />
                    </div>
                  </div>
                  <div
                    className="max-h-[200px] overflow-y-auto"
                    onWheel={(e) => {
                      const target = e.currentTarget
                      target.scrollTop += e.deltaY
                    }}
                  >
                    {filteredData.length > 0 ? (
                      filteredData.map((item) => (
                        <div
                          className="cursor-pointer p-2 hover:bg-muted"
                          key={item.id}
                          onClick={() => handleSelect(item)}
                        >
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.subtitle}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No se encontraron operadores.
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </FormControl>
            <FormMessage>{fieldState.error?.message}</FormMessage>
          </FormItem>
        )}
      />
    </div>
  )
})

SelectFormUsers.displayName = 'SelectFormUsers'
